import React, { MouseEventHandler, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import classnames from 'classnames';
import { useTheme } from '../theme';
import { IconCross } from '../icons';
import { setupRoot } from './utils';
import { ModalContextProvider, useModalContext } from './modal-context-provider';
import { Button } from '../button';

export type ModalProps = {
  /** @deprecated Please use DialogHeader and DialogHeaderTitle to add styled header */
  title?: React.ReactNode;
  children: React.ReactNode;
  open: boolean;
  /** @deprecated Property no longer used since onClose is also deprecated. Also, the new event onOverlayClick will be enough not to provide a fn. */
  disableOverlayClose?: boolean;
  /** @deprecated Please use Dialog DialogHeader in combination with DialogHeaderCloseButton to add Close button to modal header */
  hideCloseIcon?: boolean;
  rootElementId?: string;
  /** @deprecated Event will be removed as it's no longer reflecting the new Modal usage. Please use onOverlayClick for formerly overlay close event. */
  onClose?: () => void;
  onOverlayClick?: () => void;
} & React.HTMLAttributes<HTMLDivElement>;

const LegacyModal = ({
  title,
  children,
  open,
  disableOverlayClose = false,
  className,
  hideCloseIcon = false,
  rootElementId = 'airtame-modal-root',
  onClose,
  onOverlayClick,
  ...props
}: ModalProps): React.ReactElement | null => {
  const theme = useTheme();
  const [isVisible, setIsVisible] = useState(false);
  const [isMounted, setIsMounted] = useState(false);
  const { legacy } = useModalContext();

  const rootElement = useRef(setupRoot(rootElementId));
  const classNames = classnames('airtame-modal', className, {
    'airtame-modal--no-title': legacy && !title,
    'airtame-modal--open': isVisible,
    'airtame-modal--legacy': legacy,
  });
  const handleUnmount = (e: React.AnimationEvent) => {
    // Event bubbling in Portals follows the React Tree, not the DOM tree.
    // Therefore, when you open a modal, e.g. inside our SideDrawer, then
    // SideDrawer closes when user closes the new modal, due to event bubbling.
    // In this case, SideDrawer ends up in a broken state, as only
    // handleUnmount runs, all other state variables are still reflecting
    // an "open/visible" state.
    // https://reactjs.org/docs/portals.html#event-bubbling-through-portals
    e.stopPropagation();

    if (e.animationName === 'modal-close') {
      setIsMounted(false);
    }
  };

  const handleOverlayClick: MouseEventHandler = () => {
    // legacy behavior
    if (!disableOverlayClose) {
      onClose?.();
    }
    onOverlayClick?.();
  };

  useEffect(() => {
    if (open) {
      rootElement.current = setupRoot(rootElementId);
      setIsVisible(true);
      setIsMounted(true);
    } else {
      setIsVisible(false);
    }
  }, [open]);

  if (!isMounted) {
    return null;
  }

  return createPortal(
    <div
      className={classNames}
      onClick={handleOverlayClick}
      data-theme={theme}
      onAnimationEnd={handleUnmount}
      {...props}
    >
      <div onClick={(e) => e.stopPropagation()} className="airtame-modal__container">
        {legacy && (
          <div className="airtame-modal__header">
            {title && <div className="airtame-modal__title">{title}</div>}
            {!hideCloseIcon && (
              <Button
                buttonType="secondary"
                size="small"
                className="airtame-modal__close"
                onClick={onClose}
              >
                <IconCross />
              </Button>
            )}
          </div>
        )}
        {children}
      </div>
    </div>,
    rootElement.current
  );
};

/** @deprecated Please use < Dialog > as < Modal > will move to be used for internal usage */
export const Modal = (props: ModalProps) => (
  <ModalContextProvider>
    <LegacyModal {...props} />
  </ModalContextProvider>
);
Modal.displayName = 'Modal';
